/**
 * Display formatting for the public checkout.
 *
 * Two rules, both taken from the issuer pages so the public surface reads the
 * same way:
 *
 *   - **Money is formatted from base units, never re-parsed.** Every amount goes
 *     through `formatBaseUnitsGrouped`, so the digits on screen are a rendering
 *     of the exact wire string. Nothing here multiplies, divides or rounds.
 *   - **A date that cannot be parsed is shown as it arrived.** A payer comparing
 *     an invoice against the issuer's PDF needs the value the issuer sent, not a
 *     plausible-looking substitute — and `Invalid Date` is not a value.
 */

import { formatBaseUnitsGrouped, type Money } from "../money";

/** `formatMoney` with the asset symbol, for a single inline reading. */
export function payAmount(money: Money): string {
  return `${formatBaseUnitsGrouped(money.amount, money.asset.decimals)} ${money.asset.symbol}`;
}

/** A server date in the payer's locale; unparsable values pass through as sent. */
export function payDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}

/** Confirmations as "3 of 12", or just the count when the threshold is unknown. */
export function payConfirmations(have: number | null, need: number | null): string | null {
  if (have === null) return null;
  if (need === null) return String(have);
  return `${have} of ${need}`;
}
